import { useState, useContext, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ThemeContext } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Logo from "../../components/logo/Logo";
import FloatingInput from "../../components/input/FloatingInput"
import api from "../../api/axios";
import config from "../../config/config";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { useCompanyAuthPath } from "../../hooks/useCompanyAuthPath";

export default function LoginPage() {
  // ✅ Always declare hooks at the top — never conditionally
  const { t: trans } = useTranslation();
  const [i18nReady, setI18nReady] = useState(i18n.isInitialized);
  const [apiError, setApiError] = useState(null);
  const [accountDisabled, setAccountDisabled] = useState(false);
  const theme = useContext(ThemeContext);
  const navigate = useNavigate();
  const { loginUser } = useAuth();
  const { companyPath } = useCompanyAuthPath();

  // ✅ Reactively wait for i18n initialization
  useEffect(() => {
    const handleReady = () => setI18nReady(true);
    i18n.on("initialized", handleReady);
    return () => i18n.off("initialized", handleReady);
  }, []);

  // ✅ Schema should depend on i18n.language to refresh on translation load
  const schema = useMemo(
    () =>
      yup.object().shape({
        username: yup
          .string()
          .required(trans("validation_username_required"))
          .min(6, trans("validation_username_min")),
        password: yup
          .string()
          .required(trans("validation_password_required"))
          .min(6, trans("validation_password_min")),
      }),
    [trans]
  );

  // ✅ Form hook
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    try {
      setApiError(null);
      setAccountDisabled(false);

      const payload = {
        ...data,
        mainid: theme?.id,
      };

      const res = await api.post("/auth/login", payload, {
        skipAuthRedirect: true,
      });

      if (res.data.status === "success") {
        if (res.data.user && res.data.token) {
          loginUser(res.data.user, res.data.token);
        }
        // Store notify_user flag if present
        if (res.data.notify_user === true) {
          localStorage.setItem("notify_user", "true");
        }
        navigate("/dashboard");
      } else {
        setApiError(res.data.message || trans("login_invalid_credentials"));
      }
    } catch (error) {
      console.error(error);
      const status = error?.response?.status;
      const payload = error?.response?.data || {};
      const serverMsg = payload.message || payload.error || payload.msg;

      if (status === 403) {
        setAccountDisabled(true);
        setApiError(serverMsg || trans("login_disabled_message"));
      } else {
        setApiError(serverMsg || "Server error. Please try again later.");
      }
    }
  };

  // ✅ Safe early return AFTER all hooks
  if (!i18nReady) return <div>Loading translations…</div>;

  return (
    <div
      className="container-full bg-login"
      style={
        theme?.background_image
          ? {
            background: `url(${config.assetsBaseUrl}/${theme.background_image}) no-repeat center / cover`,
          }
          : {}
      }
    >
      <div className="bg-layer">
        <div className="auth-container">
          <div className="auth-screens">
            <div className="company-logo">
              <Logo type="primary" size={196} />
            </div>

            <div
              className={`auth-heading ${apiError ? "invalidcredentials" : ""}`}
            >
              <h2>{theme.main_company_name}</h2>

              {accountDisabled && (
                <p>
                  {trans("login_disabled_message")}{" "}
                  <Link to={companyPath("contact")} rel="noopener noreferrer" className="text-blue-700 underline" >{trans("login_support_link")}</Link>
                </p>
              )}

              <p className="heading-b">{trans("login_title")}</p>
              <p className="heading-b">{trans("login_subtitle")}</p>

              {apiError && <p className="field-error">{apiError}</p>}
            </div>

            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="input-field">
                <div className="form-group">
                  <FloatingInput
                    type="text"
                    name="username"
                    label={trans("login_username")}
                    className="inpt-pla"
                    {...register("username")}
                  />
                  {errors.username && (
                    <p className="field-error">{errors.username.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <FloatingInput
                    type="password"
                    name="password"
                    label={trans("login_password")}
                    className="inpt-pla"
                    {...register("password")}
                  />
                  {errors.password && (
                    <p className="field-error">{errors.password.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="submit-btn btn-bg inpt-btn"
                >
                  {isSubmitting
                    ? trans("login_signing_in")
                    : trans("login_button")}
                </button>
              </div>
            </form>

            <div className="forget-section">
              <Link to={companyPath("forgot-username")} rel="noopener noreferrer" className="heading-b" >{trans("login_forgot_username")}</Link>
              <Link to={companyPath("forgot-password")} rel="noopener noreferrer" className="heading-b" >{trans("login_forgot_password")}</Link>
            </div>

            <div className="auth-ftr">
              <p className="heading-b">
                {trans("login_register_text")}{" "}
                <Link to={companyPath("register")} rel="noopener noreferrer" className="heading-b" >{trans("login_register_link")}</Link>
              </p>
            </div>
          </div>

          {/*
          <div className="auth-screens alert-notice">
            <div className="auth-ftr">
              <img src={househodimage} />
              <h2>CostsFirst Closing Cost Calculator Discontinued at First American Title</h2>
              <p>
                Sorry, First American Title has discontinued the CostsFirst closing cost calculator and no longer offers this service.

              </p>
            </div>
          </div>*/}
          {/*    <div className="btn-continue-login">
                <button className="submit-btn btn-bg inpt-btn">Continue Login</button>
              </div> */}

          <div className="company-logo top-15">
            <Logo type="secondaryWhite" size={217} />
          </div>
        </div>
      </div>
    </div>
  );
}
